import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'
import { AlertCircle } from 'lucide-react'

import { useAuth } from '@/hooks/useAuth'
import { useLivros } from '@/hooks/useLivros'
import { Button } from '@/components/ui/button'
import { BookDetailSkeleton } from '@/components/ui/book-detail-skeleton'
import { BookDetailPage } from '@/components/book-detail-page'

import type { Livro } from '@/types/livro'

export const Route = createFileRoute('/livro/$id_pedido')({
  beforeLoad: async () => {
    const hasSession = typeof window !== 'undefined' && !!window.localStorage.getItem('sabhia-auth-cliente')
    if (!hasSession) {
      throw redirect({ to: '/' })
    }
  },
  component: LivroRouteComponent,
})

function LivroRouteComponent() {
  const navigate = useNavigate()
  const { id_pedido } = Route.useParams()

  const { cliente, loading: authLoading } = useAuth()
  const { livros, loading: livrosLoading, error } = useLivros(cliente?.id)

  const livro = useMemo<Livro | undefined>(() => {
    return livros.find((l: any) => String(l.id_pedido) === String(id_pedido))
  }, [livros, id_pedido])

  const isInitialLoading = authLoading || (livrosLoading && livros.length === 0)

  if (isInitialLoading) {
    return <BookDetailSkeleton />
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertCircle className="w-10 h-10 mx-auto text-red-500" />
          <h2 className="text-xl font-semibold">Erro ao carregar os dados</h2>
          <p className="text-muted-foreground">{error}</p>
          <div className="flex items-center justify-center gap-2">
            <Button onClick={() => window.location.reload()} className="h-12 sm:h-10">Tentar novamente</Button>
            <Button variant="outline" onClick={() => navigate({ to: '/dashboard' })} className="h-12 sm:h-10">Voltar</Button>
          </div>
        </div>
      </div>
    )
  }

  if (!livro) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6">
        <div className="max-w-md w-full text-center space-y-4">
          <AlertCircle className="w-10 h-10 mx-auto text-amber-500" />
          <h2 className="text-xl font-semibold">Livro não encontrado</h2>
          <p className="text-muted-foreground">Verifique o link ou volte para o painel.</p>
          <Button onClick={() => navigate({ to: '/dashboard' })} className="h-12 sm:h-10">Voltar ao Dashboard</Button>
        </div>
      </div>
    )
  }

  const handleVoltar = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back()
    } else {
      navigate({ to: '/dashboard' })
    }
  }

  return (
    <BookDetailPage
      livro={livro}
      onVoltar={handleVoltar}
    />
  )
}

